import { useState, useEffect } from "react";
import {
    Typography,
    Paper,
    Box,
    Grid,
    FormControl,
    Select,
    MenuItem,
    Button,
    Alert,
    CircularProgress,
    Chip,
    Stack,
    Checkbox,
    FormControlLabel,
    SelectChangeEvent,
    Divider,
    useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsIcon } from "@foodpilot/foods";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { useLocales } from "src/api/sure-api/locales/useLocales";
import { useNavigateSettingsParent } from "../useNavigateSettingsParent";

import { useGetEnabledLocales, useGetDefaultLocale, useUpdateLocales, type Locale } from "src/api/locales/useLocales";

// Default configuration - utiliser des fallbacks dynamiques
const getDefaultEnabledLocales = () => ["en", "fr"]; // Simple fallback
const getDefaultLocale = () => "en"; // Simple fallback

export const LanguageManagement = () => {
    const navigateSettingsParent = useNavigateSettingsParent();
    const { t } = useTranslation();
    const theme = useTheme();
    const { isMobile } = useResponsiveBreakpoints();

    // API calls - available languages from sure-api, persistence from locale entity
    const { locales: availableLocales, isLoading: loadingLocales, error: localesError } = useLocales();
    const { data: enabledLocalesData, isLoading: loadingEnabledLocales } = useGetEnabledLocales();
    const { data: defaultLocaleData, isLoading: loadingDefaultLocale } = useGetDefaultLocale();
    const { mutateAsync: updateLocales, isPending: savingConfig, error: saveError, isSuccess } = useUpdateLocales();

    // State management - utiliser des fallbacks dynamiques
    const [enabledLocales, setEnabledLocales] = useState<string[]>(getDefaultEnabledLocales);
    const [defaultLocale, setDefaultLocale] = useState<string>(getDefaultLocale);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Load enabled locales and default locale when available
    useEffect(() => {
        if (enabledLocalesData) {
            setEnabledLocales(enabledLocalesData.map((locale: Locale) => locale.code).filter(Boolean) as string[]);
        }
    }, [enabledLocalesData]);

    useEffect(() => {
        if (defaultLocaleData && defaultLocaleData.code) {
            setDefaultLocale(defaultLocaleData.code);
        }
    }, [defaultLocaleData]);

    // Set error state from APIs
    useEffect(() => {
        if (localesError) {
            setError("Failed to load available languages from Sure API");
        } else if (saveError) {
            setError("Failed to save language configuration");
        } else if (availableLocales.length > 0) {
            setError(null); // Clear any previous errors
        }
    }, [localesError, saveError, availableLocales]);

    // Handle save success
    useEffect(() => {
        if (isSuccess) {
            setSuccess("Language configuration saved successfully!");
            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
        }
    }, [isSuccess]);

    const handleLocaleToggle = (localeCode: string) => {
        const isCurrentlyEnabled = enabledLocales.includes(localeCode);

        if (isCurrentlyEnabled) {
            // Remove locale
            const newEnabledLocales = enabledLocales.filter((code) => code !== localeCode);
            setEnabledLocales(newEnabledLocales);

            // If we're removing the default locale, set a new default
            if (defaultLocale === localeCode && newEnabledLocales.length > 0) {
                setDefaultLocale(newEnabledLocales[0]);
            }
        } else {
            // Add locale
            setEnabledLocales([...enabledLocales, localeCode]);
        }
    };

    const handleDefaultLocaleChange = (event: SelectChangeEvent<string>) => {
        setDefaultLocale(event.target.value);
    };

    const handleGoBack = () => {
        navigateSettingsParent();
    };

    const handleSave = async () => {
        setError(null);
        setSuccess(null);

        try {
            // Get full locale data for enabled locales from sure-api
            const enabledLocaleData = availableLocales
                .filter((locale) => enabledLocales.includes(locale.code))
                .map((locale) => ({
                    code: locale.code,
                    name: locale.name,
                    nativeName: locale.nativeName,
                    flag: locale.flag,
                    direction: locale.direction,
                }));

            await updateLocales({
                locales: enabledLocaleData,
                defaultLocale,
            });
        } catch (error) {
            // Error is handled by the useEffect hook
        }
    };

    if (loadingLocales || loadingEnabledLocales || loadingDefaultLocale) {
        return (
            <Box sx={{ padding: isMobile ? "0.5rem" : "32px" }}>
                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid #E7E6E4",
                        borderRadius: "16px",
                        overflow: "hidden",
                    }}
                >
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" sx={{ p: 4 }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            {loadingLocales ?
                                t("languages.management.loadingLocales", "Loading available languages from Sure API...")
                            :   t("languages.management.loadingConfig", "Loading language configuration...")}
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: isMobile ? "0.5rem" : "32px" }}>
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "28px 32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                            onClick={handleGoBack}
                            sx={{
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                "&:hover": {
                                    backgroundColor: "#F9F9F9",
                                    borderRadius: "4px",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    "& svg": {
                                        strokeWidth: "2px",
                                        stroke: "currentColor",
                                    },
                                }}
                            >
                                <FoodsIcon icon="arrowLeftShort" size={6} />
                            </Box>
                        </Box>
                        <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                            {t("languages.management.title", "Language Management")}
                        </Typography>
                    </Stack>
                </Box>

                {/* Content */}
                <Box sx={{ padding: "32px" }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            {success}
                        </Alert>
                    )}

                    {!loadingLocales && availableLocales.length === 0 && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            {t(
                                "languages.management.noLanguagesAvailable",
                                "No languages available from Sure API. Please check your connection.",
                            )}
                        </Alert>
                    )}

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {t(
                            "languages.management.description",
                            "Configure which languages are available on this platform and set the default language for new users.",
                        )}
                    </Typography>
                    <Grid container spacing={4}>
                        {/* Available Languages Section */}
                        <Grid item xs={12} md={8}>
                            <Typography variant="h4" gutterBottom>
                                {t("languages.management.availableLanguages", "Available Languages")}
                            </Typography>

                            <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                                {availableLocales.length > 0 ?
                                    availableLocales.map((locale) => (
                                        <FormControlLabel
                                            key={locale.code}
                                            control={
                                                <Checkbox
                                                    checked={enabledLocales.includes(locale.code)}
                                                    onChange={() => handleLocaleToggle(locale.code)}
                                                    sx={{
                                                        color: "#949491",
                                                        "&.Mui-checked": {
                                                            color: theme.palette.primary[500],
                                                        },
                                                        "& .MuiSvgIcon-root": {
                                                            fontSize: 20,
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        width: "100%",
                                                        minHeight: "24px",
                                                    }}
                                                >
                                                    {locale.flag && (
                                                        <Box
                                                            sx={{
                                                                width: "24px",
                                                                textAlign: "center",
                                                                fontSize: "18px",
                                                                lineHeight: "24px",
                                                            }}
                                                        >
                                                            {locale.flag}
                                                        </Box>
                                                    )}
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            flex: 1,
                                                        }}
                                                    >
                                                        <span>{locale.name}</span>
                                                        <span style={{ color: "#666" }}>({locale.code})</span>
                                                        {locale.nativeName && locale.nativeName !== locale.name && (
                                                            <span style={{ color: "#666", fontStyle: "italic" }}>
                                                                - {locale.nativeName}
                                                            </span>
                                                        )}
                                                    </Box>
                                                </Box>
                                            }
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                mb: 1,
                                                width: "100%",
                                                margin: 0,
                                                "& .MuiFormControlLabel-label": {
                                                    fontSize: "0.95rem",
                                                    width: "100%",
                                                    marginLeft: "8px",
                                                },
                                                "& .MuiCheckbox-root": {
                                                    padding: "4px 8px 4px 4px",
                                                },
                                            }}
                                        />
                                    ))
                                :   <Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ p: 2, textAlign: "center" }}
                                        >
                                            {t(
                                                "languages.management.noLanguagesFromAPI",
                                                "No languages available from Sure API",
                                            )}
                                        </Typography>
                                    </Box>
                                }
                            </Box>
                        </Grid>

                        {/* Configuration Summary */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h4" gutterBottom>
                                {t("languages.management.configuration", "Configuration")}
                            </Typography>

                            {/* Enabled Languages Preview */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body" gutterBottom>
                                    {t("languages.management.enabledLanguages", "Enabled Languages")} (
                                    {enabledLocales.length})
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {enabledLocales.map((code) => {
                                        const locale = availableLocales.find((l) => l.code === code);
                                        return (
                                            <Chip
                                                key={code}
                                                label={`${locale?.flag || ""} ${locale?.name || code}`.trim()}
                                                size="medium"
                                                color={code === defaultLocale ? "primary" : "default"}
                                                variant={code === defaultLocale ? "filled" : "outlined"}
                                                sx={
                                                    code === defaultLocale ?
                                                        {
                                                            backgroundColor: theme.palette.primary[500],
                                                            color: "white",
                                                            fontSize: "1rem",
                                                            "&:hover": {
                                                                backgroundColor: theme.palette.primary.dark,
                                                            },
                                                        }
                                                    :   {
                                                            borderColor: theme.palette.primary[500],
                                                            color: theme.palette.primary[500],
                                                            fontSize: "1rem",
                                                            "&:hover": {
                                                                backgroundColor: `${theme.palette.primary[500]}0A`,
                                                            },
                                                        }
                                                }
                                            />
                                        );
                                    })}
                                    {enabledLocales.length === 0 && (
                                        <Typography variant="body" color="text.secondary">
                                            {t("languages.management.noLanguagesSelected", "No languages selected")}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Default Language Selection */}
                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    {t("languages.management.defaultLanguage", "Default Language")}
                                </Typography>
                                <Select
                                    value={defaultLocale}
                                    displayEmpty
                                    variant="outlined"
                                    onChange={handleDefaultLocaleChange}
                                    disabled={enabledLocales.length === 0 || availableLocales.length === 0}
                                    sx={{
                                        borderRadius: "24px",
                                        backgroundColor: "#FFFFFF",
                                        border: "1px solid #E7E6E4",
                                        height: "40px",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            border: "none",
                                        },
                                        "& .MuiSelect-select": {
                                            padding: "0px 12px 0px 20px",
                                            fontWeight: 500,
                                            fontSize: "1rem",
                                            color: "#393A36",
                                        },
                                    }}
                                >
                                    {enabledLocales.map((code) => {
                                        const locale = availableLocales.find((l) => l.code === code);
                                        return (
                                            <MenuItem key={code} value={code}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    {locale?.flag && <span>{locale.flag}</span>}
                                                    <span>{locale?.name || code}</span>
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t(
                                    "languages.management.defaultLanguageHelp",
                                    "This language will be used when a user's browser language is not available or when no preference is set.",
                                )}
                            </Typography>

                            {/* Save Button */}
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleSave}
                                disabled={savingConfig || enabledLocales.length === 0 || availableLocales.length === 0}
                                sx={{
                                    borderRadius: "44px",
                                    padding: "0px 20px",
                                    height: "40px",
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                    textTransform: "none",
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    color: "white !important",
                                    boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                    "&:hover": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                        boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                        color: "white !important",
                                    },
                                    "&:disabled": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                        opacity: 0.36,
                                        color: "white !important",
                                    },
                                }}
                            >
                                {savingConfig ?
                                    t("languages.management.saving", "Saving...")
                                :   t("languages.management.save", "Save Configuration")}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Box>
    );
};

export default LanguageManagement;
